import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";

// child
import Input from "../../ChildComponents/Input";
import BrandButton from "../../ChildComponents/BrandButton";
import Select from "../../ChildComponents/Select";

// validationSchema
import { Rolechema } from "../validation/roleValidation";

// api
import {
  useCreateMenu,
  useGetMenu,
  useUpdateMenu,
} from "../../store/api/menuApi";
import { useGetCategory } from "../../store/api/categoryApi";

function MenuForm({ data, editId, onSuccessCreated }) {
  const queryClient = useQueryClient();
  const updateMenu = useUpdateMenu(editId?._id);
  const createMenu = useCreateMenu();
  const getCategory = useGetCategory();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm({
    resolver: yupResolver(Rolechema),
  });

  const { data: categoryData } = useQuery({
    queryKey: "category",
    queryFn: getCategory,
  });

  const transformedCategory =
    categoryData?.categorys?.map((i) => ({
      label: i.name,
      value: i._id,
    })) || [];

  const [loading, setLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    if (editId && transformedCategory?.length) {
      const enrichedCategories = editId.category.map((cat) => {
        const categoryObject = cat.categoryId;
        return {
          value: categoryObject?._id,
          label: categoryObject?.name || "Unknown",
          order: cat.order,
        };
      });

      reset({
        name: editId?.name || "",
        idNumber: editId?.idNumber || "",
        category: enrichedCategories,
      });

      setSelectedCategories(enrichedCategories); // if you’re tracking category+order separately
    }
  }, [editId, reset]);

  const mutation = useMutation({
    mutationFn: (data) => {
      if (editId) {
        return updateMenu(data);
      } else {
        return createMenu(data);
      }
    },
    onSuccess: () => {
      setLoading(false);
      toast.success("Created Successfully");
      reset();
      setSelectedCategories([]);
      onSuccessCreated(1);
      queryClient.invalidateQueries({ queryKey: ["menu-data"] });
    },
    onError: (error) => {
      toast.error(error.message);
      setLoading(false);
    },
  });

  const handleCategoryChange = (selected) => {
    const updated =
      selected?.map((item, index) => {
        const existing = selectedCategories.find((c) => c.value === item.value);
        return {
          ...item,
          order: existing?.order || index + 1,
        };
      }) || [];
    setSelectedCategories(updated);
  };

  const handleOrderChange = (index, newOrder) => {
    const updated = [...selectedCategories];
    updated[index].order = Number(newOrder);
    setSelectedCategories(updated);
  };

  const onSubmit = (formData) => {
    setLoading(true);

    const categories = selectedCategories.map((cat) => ({
      categoryId: cat.value,
      order: cat.order,
    }));

    try {
      mutation.mutate({
        name: formData.name,
        idNumber: formData.idNumber,
        category: categories,
      });
    } catch (error) {
      setLoading(false);
      toast.error(error.message);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-sm-9">
            <div className="row">
              <div className="col-sm-4">
                <Input
                  icon="title"
                  {...register("name")}
                  error={errors.name?.message}
                  type="text"
                  placeholder="Top, Side Bar, Footer"
                  label="Menu Name"
                />
              </div>

              <div className="col-sm-4">
                <Input
                  icon="title"
                  {...register("idNumber")}
                  error={errors.idNumber?.message}
                  type="text"
                  placeholder="ex: 1-20"
                  label="ID Number"
                />
              </div>

              <div className="col-sm-4">
                <Select
                  isClearable
                  isMulti
                  options={transformedCategory}
                  placeholder="Select Category"
                  onChange={handleCategoryChange}
                  value={transformedCategory.filter((item) =>
                    selectedCategories.map((c) => c.value).includes(item.value)
                  )}
                />
              </div>

              {/* Order Inputs */}
              <div className="col-sm-12 my-5 shadow p-3 bg-white">
                <h6 className="fw-bold pb-1 mb-3 border-bottom border-dark">
                  Set Order for Selected Categories
                </h6>
                {selectedCategories.map((cat, index) => (
                  <div
                    key={cat.value}
                    className=" ps-2 pb-2 d-flex align-items-center gap-2 mb-2"
                  >
                    <strong>{cat.label}</strong>
                    <input
                      type="number"
                      min={1}
                      value={cat.order}
                      onChange={(e) => handleOrderChange(index, e.target.value)}
                      className="form-control w-auto"
                    />
                  </div>
                ))}
              </div>

              <BrandButton
                type="submit"
                title={loading ? "Saving..." : "Save Changes"}
                className="my-4"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default MenuForm;
